import React, { useCallback } from 'react'

import { Button } from '@refera/ui-web'
import { navigate, useLocation, useParams } from '@reach/router'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { useToast } from '_/hooks/use-toast'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import { useFormContext } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  createOpenProposeBudget,
  getStepStatusLog,
  updateBudget,
  updateBudgetResolution,
  getBudgetById,
} from '_/modules/budget/actions'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { parse } from 'query-string'
import { ROUTES } from '_/utils/constants'
import {
  createFormDataBudgetResolution,
  sanitizeResolution,
} from '_/views/budget-view-v2/utils/functions'
import { verifyIfBudgetIsSent } from '_/utils/verify-if-budget-is-sent'
import { getServiceOrder } from '_/modules/service-orders/actions'

export default function SaveButton() {
  const dispatch = useDispatch()
  const { serviceOrderId, budgetId } = useParams()
  const { pathname, search } = useLocation()
  const { tipo: budgetType } = parse(search)
  const {
    isEditing,
    setLoading,
    setIsEditing,
    setBudgetResolutionToEdit,
    setAddProblemReadOnly,
    budget,
    setResetForm,
    setPage,
  } = useBudgetForm()
  const { isAdmin, isSAAS, isAdminSAAS, checkIfUserDoesNotPermission } = useRolePermission()
  const { showToast } = useToast()
  const { getValues, handleSubmit } = useFormContext()

  const isCreating = pathname.includes('/novo')

  const isValidSAASExternalBudgetType =
    budgetType === 'externo' || (budget && budget?.proposeOpened === false)
  const isValidSAASInternalBudgetType =
    budgetType === 'interno' || (budget && budget?.proposeOpened === true)

  const isBudgetSent = verifyIfBudgetIsSent(budget)

  const canProviderOrSAASSendBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.PROVIDER_SEND_BUDGET,
  })

  const canSAASSendInnerBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.ADD_INTERNAL_BUDGET,
  })

  const canReferaSendBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.SELECT_TRADESMAN,
  })

  const canReferaOrSaasEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET,
  })

  const canSAASSendBudgetByType = () => {
    if (!isSAAS) return true
    if (isValidSAASInternalBudgetType) return canSAASSendInnerBudget
    if (isValidSAASExternalBudgetType) return canProviderOrSAASSendBudget
    return true
  }

  const showSaveButton =
    !isCreating && isEditing && (isAdmin || (isSAAS && isValidSAASInternalBudgetType))

  const updateBudgetResolutions = useCallback(
    async budgetResolutions => {
      try {
        const promises = budgetResolutions.map(resolution => {
          const clean = sanitizeResolution(resolution)
          const formData = createFormDataBudgetResolution(clean)

          return dispatch(updateBudgetResolution(budgetId, formData))
        })

        await Promise.all(promises)
      } catch (error) {
        setLoading(false)
        throw error
      }
    },
    [dispatch, budgetId]
  )

  function handleValidationError() {
    showToast({
      type: 'error',
      message: 'Um ou mais dados do formulário possuem problemas.',
    })
  }

  const handleSave = useCallback(async () => {
    let errorMessage = 'Você não tem permissão para esta ação.'

    if (!budget || !(budget?.budgetResolution?.length > 0)) {
      showToast({
        type: 'error',
        message: 'Só se pode Salvar um orçamento após ter sido inserido problemas/serviços.',
      })
      return
    }

    // validate by permission
    if (!isAdminSAAS) {
      const unauthorized = checkIfUserDoesNotPermission(
        PERMISSIONS.BUDGETS,
        PERMISSIONS_ACTIONS.EDIT
      )

      // if unauthorized, shows toast and return
      if (unauthorized) {
        return
      }
    }

    let invalidStep = false

    if (isBudgetSent) {
      invalidStep = !canReferaOrSaasEditBudget
    } else {
      invalidStep = !canSAASSendBudgetByType()
    }

    if (invalidStep) {
      errorMessage = 'Este chamado está em uma etapa que não permite incluir orçamento.'
    }

    if ((!isSAAS && !isAdmin) || invalidStep) {
      showToast({ message: errorMessage, type: 'error' })
      return
    }

    const payload = getValues()
    const payloadWithoutUi = Object.keys(payload).reduce((acc, key) => {
      if (!key.startsWith('__')) acc[key] = payload[key]
      return acc
    }, {})
    // removing fields that are not needed in the payload
    const { budgetResolutions, ...payloadToSend } = payloadWithoutUi

    if (budgetId) {
      // already has budget
      const options = { transformPayload: true, transformFormData: true, formData: true }
      payloadToSend.newBudgetResolutionsOrder = budgetResolutions.map(({ id }) => id).join(',')
      payloadToSend.visitHappenedTemp = String(payloadToSend.visitHappenedTemp)

      // Add the 'saving' parameter only if the budget is in a "propose opened" state and is not sent.
      if (budget?.proposeOpened && !isBudgetSent) payloadToSend.saving = true

      setLoading(true)
      if (budgetResolutions?.length > 0) {
        try {
          await updateBudgetResolutions(budgetResolutions)
        } catch (error) {
          setLoading(false)
          showToast({
            message: formatErrorMessage(error),
            type: 'error',
          })
          return
        }
      }
      try {
        await dispatch(updateBudget(serviceOrderId, budgetId, payloadToSend, options)).then(
          async () => {
            const params = { budgetView: true, showOriginalBudget: false }
            await dispatch(getBudgetById(budgetId, params))
          }
        )
        setBudgetResolutionToEdit(null)
        setResetForm(true)
        await Promise.all([
          dispatch(getServiceOrder(serviceOrderId)),
          dispatch(getStepStatusLog(serviceOrderId)),
        ])
        showToast({ type: 'success' })
        // to remove edit parameter from url
        setAddProblemReadOnly(false)
        navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${budgetId}`)
        setPage('default')
      } catch (error) {
        showToast({
          message: formatErrorMessage(error),
          type: 'error',
        })
      } finally {
        setLoading(false)
        setIsEditing(false)
      }
    } else {
      // does not have budget
      setLoading(true)
      dispatch(createOpenProposeBudget(serviceOrderId, payloadToSend))
        .then(newBudgetId => {
          showToast({ type: 'success' })
          navigate(`${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudgetId}`)
        })
        .catch(error =>
          showToast({
            message: formatErrorMessage(error),
            type: 'error',
          })
        )
        .finally(() => setLoading(false))
    }
  }, [
    isCreating,
    isSAAS,
    isAdmin,
    isAdminSAAS,
    canProviderOrSAASSendBudget,
    canReferaSendBudget,
    checkIfUserDoesNotPermission,
    canReferaOrSaasEditBudget,
    budgetId,
    getValues,
    setLoading,
    dispatch,
    showToast,
    serviceOrderId,
    setIsEditing,
    budget,
    isBudgetSent,
    canSAASSendBudgetByType,
  ])

  return (
    showSaveButton && (
      <Button
        onClick={handleSubmit(handleSave, handleValidationError)}
        color="primary"
        variant="primary"
      >
        Salvar
      </Button>
    )
  )
}
